export const MARKETING_QUERY_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const;

export const MARKETING_CLICK_ID_PARAMS = [
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
] as const;

export type MarketingQueryParam = (typeof MARKETING_QUERY_PARAMS)[number];
export type MarketingClickIdParam = (typeof MARKETING_CLICK_ID_PARAMS)[number];

export type MarketingTouchpoint = {
  capturedAt: string;
  landingPath: string;
  referrerUrl: string | null;
  referrerHost: string | null;
  utm: Partial<Record<MarketingQueryParam, string>>;
  clickIds: Partial<Record<MarketingClickIdParam, string>>;
};

export type MarketingAttribution = {
  version: 1;
  firstTouch: MarketingTouchpoint;
  lastTouch: MarketingTouchpoint;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseStringRecord<TKeys extends readonly string[]>(
  value: unknown,
  allowedKeys: TKeys,
): Partial<Record<TKeys[number], string>> {
  if (!isRecord(value)) return {};

  const allowed = new Set<string>(allowedKeys);
  const parsed: Partial<Record<TKeys[number], string>> = {};

  for (const [key, rawValue] of Object.entries(value)) {
    if (!allowed.has(key) || typeof rawValue !== "string" || rawValue.length === 0) {
      continue;
    }

    parsed[key as TKeys[number]] = rawValue;
  }

  return parsed;
}

function parseTouchpoint(value: unknown): MarketingTouchpoint | null {
  if (!isRecord(value)) return null;

  const capturedAt = typeof value.capturedAt === "string" ? value.capturedAt : null;
  const landingPath = typeof value.landingPath === "string" ? value.landingPath : null;
  const referrerUrl =
    value.referrerUrl === null || typeof value.referrerUrl === "string"
      ? value.referrerUrl
      : null;
  const referrerHost =
    value.referrerHost === null || typeof value.referrerHost === "string"
      ? value.referrerHost
      : null;

  if (!capturedAt || !landingPath) return null;

  return {
    capturedAt,
    landingPath,
    referrerUrl,
    referrerHost,
    utm: parseStringRecord(value.utm, MARKETING_QUERY_PARAMS),
    clickIds: parseStringRecord(value.clickIds, MARKETING_CLICK_ID_PARAMS),
  };
}

export function parseMarketingAttribution(
  value: string | null | undefined,
): MarketingAttribution | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    if (!isRecord(parsed) || parsed.version !== 1) return null;

    const firstTouch = parseTouchpoint(parsed.firstTouch);
    const lastTouch = parseTouchpoint(parsed.lastTouch);

    if (!firstTouch || !lastTouch) return null;

    return {
      version: 1,
      firstTouch,
      lastTouch,
    };
  } catch {
    return null;
  }
}

export function stringifyMarketingAttribution(value: MarketingAttribution): string {
  return JSON.stringify(value);
}

export function hasMarketingSignal(touchpoint: MarketingTouchpoint): boolean {
  return (
    Boolean(touchpoint.referrerHost) ||
    Object.keys(touchpoint.utm).length > 0 ||
    Object.keys(touchpoint.clickIds).length > 0
  );
}
